import { of, from, EMPTY } from "rxjs";
import { switchMap } from "rxjs/operators";
import { ofType } from "redux-observable";
import appConfig from "@/appConfig";
import { sessionError, SESSION_LOADED } from "@dux/session";
import { mapObject } from "@helper/object";
import deepMerge from "@helper/deepMerge";
import { toastPush } from "@dux/toast";

export const LOCALIZATION_SESSION_UPDATE = "pss/localization/SESSION_UPDATE";
export const LOCALIZATION_ENABLE_EDIT_MODE =
  "pss/localization/ENABLE_EDIT_MODE";
export const LOCALIZATION_TOGGLE_EDIT_MODE =
  "pss/localization/TOGGLE_EDIT_MODE";
export const LOCALIZATION_DISABLE_EDIT_MODE =
  "pss/localization/DISABLE_EDIT_MODE";
export const LOCALIZATION_CLAIM_EDITING = "pss/localization/CLAIM_EDITING";
export const LOCALIZATION_RELEASE_EDITING = "pss/localization/RELEASE_EDITING";
export const LOCALIZATION_APPLY_EDIT = "pss/localization/APPLY_EDIT";
export const LOCALIZATION_REVERT_EDIT = "pss/localization/REVERT_EDIT";
export const LOCALIZATION_REVERT_ALL = "pss/localization/REVERT_ALL";
export const LOCALIZATION_SAVE = "pss/localization/SAVE";
export const LOCALIZATION_SAVE_SUCCESS = "pss/localization/SAVE_SUCCESS";
export const LOCALIZATION_SAVE_ERROR = "pss/localization/SAVE_ERROR";
export const LOCALIZATION_CACHE_KEYS = "pss/localization/CACHE_KEYS";
export const LOCALIZATION_UPDATE_PAGES = "pss/localization/UPDATE_PAGES";

const initialState = {
  sessionData: null,
  editing: false,
  editingClaim: null,
  stringOverrides: {},
  pageOverrides: {},
  saving: false,
  saveError: null,
  keyCache: [],
  pages: [],
  lastEditedPage: null,
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case LOCALIZATION_SESSION_UPDATE:
      return {
        ...state,
        sessionData: action.payload,
        editing: false,
        editingClaim: null,
        stringOverrides: {},
      };
    case LOCALIZATION_ENABLE_EDIT_MODE:
      return { ...state, editing: true, editingClaim: null };
    case LOCALIZATION_TOGGLE_EDIT_MODE:
      return { ...state, editing: !state.editing, editingClaim: null };
    case LOCALIZATION_DISABLE_EDIT_MODE:
      return { ...state, editing: false, editingClaim: null };
    case LOCALIZATION_CLAIM_EDITING:
      return { ...state, editingClaim: action.payload };
    case LOCALIZATION_RELEASE_EDITING:
      if (action.payload && action.payload !== state.editingClaim) return state;
      return { ...state, editingClaim: null };
    case LOCALIZATION_APPLY_EDIT: {
      const pageOverrides = {
        ...state.pageOverrides,
        [action.payload.page]: {
          ...state.pageOverrides[action.payload.page],
          [action.payload.key]: action.payload.value,
        },
      };
      return {
        ...state,
        pageOverrides,
        lastEditedPage: action.payload.page,
        stringOverrides: deepMerge(
          state.pages.map((page) => pageOverrides[page] || {})
        ),
      };
    }
    case LOCALIZATION_REVERT_EDIT: {
      const pageOverrides = mapObject(state.pageOverrides, (page) => {
        let result = { ...page };
        delete result[action.payload.key];
        return result;
      });

      return {
        ...state,
        pageOverrides,
        stringOverrides: deepMerge(
          state.pages.map((page) => pageOverrides[page] || {})
        ),
      };
    }
    case LOCALIZATION_SAVE:
      return {
        ...state,
        saving: true,
        saveError: null,
      };
    case LOCALIZATION_REVERT_ALL:
    case LOCALIZATION_SAVE_SUCCESS:
      return {
        ...state,
        saving: false,
        saveError: null,
        editing: false,
        editingClaim: null,
        pageOverrides: {},
        stringOverrides: {},
      };
    case LOCALIZATION_SAVE_ERROR:
      return {
        ...state,
        saveError: action.payload,
      };
    case LOCALIZATION_CACHE_KEYS:
      if (
        state.keyCache.some(({ keys }) => {
          return keys.every((k, i) => k === action.payload.keys[i]);
        })
      )
        return state;
      const newCache = state.keyCache.slice(0, appConfig.localKeyCacheSize - 1);
      newCache.unshift(action.payload);
      return { ...state, keyCache: newCache };
    case LOCALIZATION_UPDATE_PAGES:
      return {
        ...state,
        pages: action.payload,
        lastEditedPage: action.payload[0] || null,
      };
    default:
      return state;
  }
}

export function localizationSessionUpdate(data) {
  return { type: LOCALIZATION_SESSION_UPDATE, payload: data };
}

export function localizationEnableEditMode() {
  return { type: LOCALIZATION_ENABLE_EDIT_MODE };
}
export function localizationToggleEditMode() {
  return { type: LOCALIZATION_TOGGLE_EDIT_MODE };
}

export function localizationDisableEditMode() {
  return { type: LOCALIZATION_DISABLE_EDIT_MODE };
}

export function localizationClaimEditing(id) {
  return { type: LOCALIZATION_CLAIM_EDITING, payload: id };
}

export function localizationReleaseEditing(id) {
  return { type: LOCALIZATION_RELEASE_EDITING, payload: id };
}

export function localizationApplyEdit({ key, value, page }) {
  return { type: LOCALIZATION_APPLY_EDIT, payload: { key, value, page } };
}

export function localizationRevertEdit(key) {
  return { type: LOCALIZATION_REVERT_EDIT, payload: { key } };
}

export function localizationRevertAll() {
  return { type: LOCALIZATION_REVERT_ALL };
}

export function localizationSave() {
  return { type: LOCALIZATION_SAVE };
}

export function localizationCacheKeys({ key, keys, context, isIcon }) {
  return {
    type: LOCALIZATION_CACHE_KEYS,
    payload: { timestamp: Date.now(), key, keys, context, isIcon },
  };
}

export function localizationUpdatePages(pages) {
  return {
    type: LOCALIZATION_UPDATE_PAGES,
    payload: pages,
  };
}

function localizationSaveSuccess() {
  return { type: LOCALIZATION_SAVE_SUCCESS };
}

function localizationSaveError(error) {
  return { type: LOCALIZATION_SAVE_ERROR, payload: error };
}

export function loadPagesEpic(action$, store$, { api }) {
  return appConfig.sessionOverride
    ? EMPTY
    : action$.pipe(
        ofType(SESSION_LOADED),
        switchMap(() => {
          if (!store$.value.session.isAdministrator) return EMPTY;
          return from(api.safeFetchGet("/Localization/Pages")).pipe(
            switchMap((result) => {
              if (result.error) return of(sessionError(result.error));
              return of(localizationUpdatePages(result.data.pages));
            })
          );
        })
      );
}

export function saveEpic(action$, store$, { api }) {
  return action$.pipe(
    ofType(LOCALIZATION_SAVE),
    switchMap(() => {
      const { currentCulture } = store$.value.session;
      const { pageOverrides } = store$.value.localization;
      const savePages = Object.keys(pageOverrides);

      const requests = savePages.map((page, idx) => {
        const values = pageOverrides[page];
        return api.safeFetchPut(`/Localization/${currentCulture}/Keys`, {
          reload: idx === savePages.length - 1,
          page,
          values,
        });
      });

      return from(Promise.all(requests)).pipe(
        switchMap((responses) => {
          const failed = responses.filter((r) => !r.data.success);
          if (failed.length > 0)
            return of(localizationSaveError(failed[0].error));
          return of(
            localizationSaveSuccess(),
            toastPush({
              localKey: "::PSS::Toast::LocalizationSaved",
            })
          );
        })
      );
    })
  );
}

export const localizationEpics = [loadPagesEpic, saveEpic];
